import * as Sentry from '@sentry/vue';
import { ref } from 'vue';
import type { Ref } from 'vue';
import type { PurchaseResponse, PaymentMethodResponse } from '@@/types/api';
import { ApiError } from '~/composables/api/api-error';
import { usePurchasesApi } from '~/composables/api/use-purchases-api';
import { useStripeApi } from '~/composables/api/use-stripe-api';
import { useMetaPixel } from '~/composables/use-meta-pixel';
import { buildMetaConversionContext } from '~/utils/meta-conversion-context';
import type { MetaConversionContext } from '~/utils/meta-conversion-context';
import { runPostSuccess } from '~/utils/run-post-success';

interface UsePurchaseActionsOptions {
  eventId: Ref<string>;
  purchase: Ref<PurchaseResponse | null>;
  enrich: () => Promise<void>;
  refreshPublicEvent: () => Promise<void>;
}

export function usePurchaseActions(options: UsePurchaseActionsOptions) {
  const { eventId, enrich, refreshPublicEvent } = options;
  const purchasesApi = usePurchasesApi();
  const stripeApi = useStripeApi();
  const { trackEvent } = useMatomo();
  const { trackInitiateCheckout, trackPurchase } = useMetaPixel();

  // Dialog visibility
  const showPurchaseDialog = ref(false);

  // Loading state
  const isPurchasing = ref(false);

  // Payment methods
  const paymentMethods = ref<PaymentMethodResponse[]>([]);
  const isLoadingPaymentMethods = ref(false);

  // Idempotency key — preserved across retries
  const purchaseIdempotencyKey = ref('');

  // Snackbar state
  const snackbar = ref(false);
  const snackbarText = ref('');
  const snackbarColor = ref('success');

  function showSnackbar(text: string, color = 'success') {
    snackbarText.value = text;
    snackbarColor.value = color;
    snackbar.value = true;
  }

  async function fetchPaymentMethods() {
    isLoadingPaymentMethods.value = true;
    try {
      const response = await stripeApi.listPaymentMethods();
      paymentMethods.value = response.data;
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      isLoadingPaymentMethods.value = false;
    }
  }

  function handleCardAdded() {
    fetchPaymentMethods();
  }

  function openPurchaseDialog() {
    purchaseIdempotencyKey.value = crypto.randomUUID();
    showPurchaseDialog.value = true;
    fetchPaymentMethods();
    trackEvent('Purchase', 'DialogOpened', eventId.value);
    trackInitiateCheckout({
      eventId: eventId.value,
      eventID: crypto.randomUUID(),
      contentType: 'purchase',
    });
  }

  async function handlePurchaseSubmit(
    paymentMethodId: string,
    registrationTypeId: string,
    partySize: number = 1,
  ) {
    isPurchasing.value = true;

    // Built BEFORE the purchase's `try` on purpose. If this ran inside it and
    // ever threw, that catch would report to Sentry and tell the user
    // "Failed to purchase ticket." for a purchase that actually succeeded.
    // `buildMetaConversionContext` is contracted never to throw, and this
    // placement makes that failure mode structurally impossible rather than
    // merely unlikely.
    //
    // The local catch is defence in depth for the other half of the hazard: a
    // throw here must not stop the purchase from being attempted at all. Meta
    // attribution is never worth failing a payment over. Reported as a warning
    // (not an exception) because the payment itself is unaffected.
    let conversionContext: MetaConversionContext = {};
    try {
      conversionContext = buildMetaConversionContext();
    } catch {
      Sentry.captureMessage(
        'buildMetaConversionContext threw — purchase sent without Meta click identity',
        {
          level: 'warning',
          tags: { operation: 'purchase_ticket', event_id: eventId.value },
        },
      );
    }

    // The outer `try`/`finally` owns `isPurchasing` and resets it only after
    // the post-success refresh settles, so the loading flag spans the whole
    // action.
    try {
      // Only the API call lives in the inner `try`. Everything after it
      // (tracking, refresh) runs through `runPostSuccess`, because a throw
      // there would otherwise land in that `catch`, be reported to Sentry as
      // `operation: 'purchase_ticket'`, and tell the user "Failed to purchase
      // ticket." for a card that was already charged — exactly what happened
      // when the response was read as `{ data }` but arrived unwrapped
      // (#1980). A user who believes the charge failed buys again.
      let purchased: PurchaseResponse;
      try {
        purchased = await purchasesApi.purchaseTicket(
          eventId.value,
          {
            // Spread FIRST so a future key collision can never overwrite a
            // payment field.
            ...conversionContext,
            payment_method_id: paymentMethodId,
            registration_type_id: registrationTypeId,
            party_size: partySize,
          },
          purchaseIdempotencyKey.value,
        );
      } catch (e) {
        Sentry.captureException(e, {
          tags: { operation: 'purchase_ticket', event_id: eventId.value },
        });
        const isNetwork = e instanceof ApiError && e.isNetworkError;
        showSnackbar(
          isNetwork
            ? 'Network error — check your connection and try again.'
            : 'Failed to purchase ticket. Please try again.',
          'error',
        );
        return;
      }

      showPurchaseDialog.value = false;
      showSnackbar("Ticket purchased! You're all set for this event.");

      await runPostSuccess('purchase_ticket', eventId.value, () => {
        trackEvent('Purchase', 'Completed', eventId.value);
        trackPurchase({
          eventId: eventId.value,
          valueCents: purchased.total_charged,
          currency: 'USD',
          eventID: purchased.id,
          contentType: 'purchase',
        });
      });
      await runPostSuccess('purchase_ticket', eventId.value, () =>
        Promise.all([enrich(), refreshPublicEvent()]),
      );
    } finally {
      isPurchasing.value = false;
    }
  }

  return {
    showPurchaseDialog,
    isPurchasing,
    paymentMethods,
    isLoadingPaymentMethods,
    handleCardAdded,
    purchaseIdempotencyKey,
    snackbar,
    snackbarText,
    snackbarColor,
    openPurchaseDialog,
    handlePurchaseSubmit,
  };
}
